import React, { createContext, useContext, useState, useEffect } from 'react';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    localization: {
      locale: 'en-US',
      timeZone: 'UTC',
      dateFormat: 'MM/dd/yyyy',
      timeFormat12h: true,
      numberFormat: 'locale',
      textDirection: 'LTR'
    },
    currency: {
      primaryCurrency: 'USD',
      showLocalCurrency: true,
      autoConversion: true,
      roundingMode: 'HALF_UP',
      fxSource: 'provider',
      fxProvider: 'ECB',
      fxRefreshMinutes: 60,
      exchangeRates: {}
    },
    accessibility: {
      theme: 'system'
    },
    compliance: {
      privacyFrameworks: ['None'],
      cookieConsent: {
        enabled: true,
        categories: {
          necessary: true,
          analytics: false,
          marketing: false
        }
      }
    },
    notifications: {
      channels: {
        email: true,
        sms: false,
        push: false
      },
      quietHours: {
        start: '22:00',
        end: '07:00',
        timeZone: 'UTC'
      }
    },
    backup: {
      exportFormats: ['JSON'],
      restorePointsEnabled: true,
      googleDrive: {
        enabled: false,
        oauthClientId: '',
        targetFolderId: ''
      }
    }
  });

  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState('system');

  // Load settings from API
  useEffect(() => {
    loadSettings();
  }, []);

  // Apply theme changes
  useEffect(() => {
    applyTheme(settings.accessibility.theme);
  }, [settings.accessibility.theme]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Transform API response to settings structure
        const transformedSettings = {
          localization: data.localization?.data || settings.localization,
          currency: data.currency?.data || settings.currency,
          accessibility: data.accessibility?.data || settings.accessibility,
          compliance: data.compliance?.data || settings.compliance,
          notifications: data.notifications?.data || settings.notifications,
          backup: data.backup?.data || settings.backup
        };

        setSettings(transformedSettings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (sectionKey, newData) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) throw new Error('No authentication token');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/user`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          section_key: sectionKey,
          data: newData
        })
      });

      if (response.ok) {
        // Update local state
        setSettings(prev => ({
          ...prev,
          [sectionKey]: newData
        }));

        return true;
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  };

  const applyTheme = (themeMode) => {
    const root = document.documentElement;
    
    // Remove existing theme classes
    root.classList.remove('light', 'dark');
    
    let appliedTheme = themeMode;
    
    // Handle system theme
    if (themeMode === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      appliedTheme = prefersDark ? 'dark' : 'light';
    }
    
    // Apply theme class
    root.classList.add(appliedTheme);
    
    // Set CSS custom properties for theme
    if (appliedTheme === 'dark') {
      root.style.setProperty('--bg-primary', '#1a1a1a');
      root.style.setProperty('--bg-secondary', '#2d2d2d');
      root.style.setProperty('--text-primary', '#ffffff');
      root.style.setProperty('--text-secondary', '#cccccc');
      root.style.setProperty('--border-color', '#404040');
      root.style.setProperty('--accent-color', '#3b82f6');
    } else {
      root.style.setProperty('--bg-primary', '#ffffff');
      root.style.setProperty('--bg-secondary', '#f8f9fa');
      root.style.setProperty('--text-primary', '#000000');
      root.style.setProperty('--text-secondary', '#6b7280');
      root.style.setProperty('--border-color', '#e5e7eb');
      root.style.setProperty('--accent-color', '#3b82f6');
    }
    
    setTheme(appliedTheme);
  };

  // Format functions based on settings
  const formatDate = (date, customFormat) => {
    const format = customFormat || settings.localization.dateFormat;
    const locale = settings.localization.locale;
    
    try {
      // Convert format string to Intl.DateTimeFormat options
      let options = {};
      
      if (format.includes('yyyy')) {
        options.year = 'numeric';
      } else if (format.includes('yy')) {
        options.year = '2-digit';
      }
      
      if (format.includes('MM')) {
        options.month = '2-digit';
      } else if (format.includes('M')) {
        options.month = 'numeric';
      }
      
      if (format.includes('dd')) {
        options.day = '2-digit';
      } else if (format.includes('d')) {
        options.day = 'numeric';
      }
      
      return new Date(date).toLocaleDateString(locale, options);
    } catch (error) {
      return new Date(date).toLocaleDateString();
    }
  };

  const formatTime = (date) => {
    const locale = settings.localization.locale;
    const hour12 = settings.localization.timeFormat12h;
    
    return new Date(date).toLocaleTimeString(locale, { hour12 });
  };

  const formatNumber = (number) => {
    const locale = settings.localization.locale;
    return number.toLocaleString(locale);
  };

  const formatCurrency = async (amount, currency) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/currency/format`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount,
          currency,
          locale: settings.localization.locale,
          show_local_currency: settings.currency.showLocalCurrency,
          local_currency: settings.currency.primaryCurrency
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.formatted;
      }
    } catch (error) {
      console.error('Error formatting currency:', error);
    }
    
    // Fallback formatting
    return `${currency} ${amount.toLocaleString(settings.localization.locale)}`;
  };

  const value = {
    settings,
    loading,
    theme,
    updateSettings,
    loadSettings,
    applyTheme,
    formatDate,
    formatTime,
    formatNumber,
    formatCurrency
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};